import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";

import { memo, useEffect, useRef, useState } from "react";
import { Navigation, Thumbs } from "swiper/modules";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";

import BB_Button from "@/Components/BB_Button";
import BB_ImageWrapper from "@/Components/BB_ImageWrapper";
import BB_Select from "@/Components/BB_Select";
import BellIcon from "@/Icons/bell.svg?react";
import CartIcon from "@/Icons/cart.svg?react";
import ChevronLeftIcon from "@/Icons/chevron_left.svg?react";
import ChevronRightIcon from "@/Icons/chevron_right.svg?react";
import RemoveBellIcon from "@/Icons/removeBell.svg?react";
import SpinnerIcon from "@/Icons/spinner.svg?react";
import BB_Link from "@/types/BB_Link";
import BB_Product from "@/types/BB_Product";

export type BB_ProductHeroProps = {
	product: BB_Product;
	parent_product?: BB_Product | null;
	product_siblings?: BB_Product[] | null;
	isSubscribeAllLoading: boolean;
	handleSubscribleAll: () => void;
	isFollowedAll: boolean;
	buybox_link?: BB_Link | null;
	buttonsDisabled: boolean;
};

const BB_ProductHero = (props: BB_ProductHeroProps) => {
	const showThumbs = (props.product.images?.length ?? 0) > 1;
	const prevRef = useRef(null);
	const nextRef = useRef(null);

	const [descExpanded, setDescExpanded] = useState(false);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const descRef = useRef<HTMLParagraphElement>(null);

	const [thumbsSwiper, setThumbsSwiper] = useState<string | SwiperClass | null | undefined>();

	useEffect(() => {
		const checkOverflow = () => {
			if (descRef.current) {
				setIsOverflowing(descRef.current.scrollHeight > descRef.current.clientHeight);
			}
		};

		checkOverflow();

		if (typeof window !== "undefined") {
			window.addEventListener("resize", checkOverflow);
			return () => window.removeEventListener("resize", checkOverflow);
		}
	}, []);

	return (
		<section id="productHero" className="flex flex-col items-center max-lg:gap-6 lg:flex-row">
			{/* Left Side: Product Image */}
			<div className={"max-h-85 w-full px-3 select-none sm:px-6 lg:w-1/3 lg:pr-0"}>
				<div className="relative">
					<Swiper
						loop={showThumbs}
						grabCursor
						spaceBetween={15}
						navigation={{
							prevEl: prevRef.current,
							nextEl: nextRef.current
						}}
						thumbs={{
							swiper: showThumbs ? thumbsSwiper : undefined,
							slideThumbActiveClass: "border-secondary-500!"
						}}
						modules={showThumbs ? [Navigation, Thumbs] : []}
						className="h-65 w-full overflow-hidden rounded-lg">
						{props.product.images?.length === 0 && (
							<SwiperSlide
								key={"defaultImage"}
								className="flex items-center justify-center rounded-lg bg-white shadow-lg">
								<BB_ImageWrapper
									className="mx-auto h-65 w-fit object-contain select-none"
									alt="Produkt"
								/>
							</SwiperSlide>
						)}
						{props.product.images?.map((image, index) => (
							<SwiperSlide
								key={
									image.url
										? "i_" + image.url + index.toString()
										: "i_" + index.toString()
								}
								className="flex items-center justify-center rounded-lg bg-white shadow-lg">
								<BB_ImageWrapper
									bb_image={image}
									suffix="?class=6"
									addPadding={image.trimmed === 1}
									className="mx-auto h-65 w-fit object-contain select-none"
									alt="Produkt"
								/>
							</SwiperSlide>
						))}
					</Swiper>
					{showThumbs && (
						<>
							<Swiper
								onSwiper={setThumbsSwiper}
								grabCursor
								spaceBetween={5}
								slidesPerView={5}
								watchSlidesProgress
								modules={[Thumbs]}
								className="mt-2 h-16 w-full overflow-hidden rounded-lg">
								{/* https://github.com/nolimits4web/swiper/issues/7809#issuecomment-2615673384 */}
								{props.product.images?.map((image, index) => (
									<SwiperSlide
										key={image.url ? `${image.url}?class=1` : index.toString()}
										className="border-background-100 dark:border-dark-background-300 h-16 overflow-hidden rounded-lg border-2 shadow-lg">
										<BB_ImageWrapper
											bb_image={image}
											suffix="?class=1"
											addPadding={image.trimmed === 1}
											className="h-16 w-full bg-white bg-clip-padding object-contain select-none"
											alt="Product Thumbnail Preview"
										/>
									</SwiperSlide>
								))}
							</Swiper>
							<div
								ref={prevRef}
								className="bg-background-50/80 dark:bg-dark-background-300/70 absolute top-2/6 left-2 z-10 transform cursor-pointer rounded-full p-2 shadow-lg backdrop-blur-xl">
								<ChevronLeftIcon className="h-6 w-6 text-black dark:text-white" />
							</div>
							<div
								ref={nextRef}
								className="bg-background-50/80 dark:bg-dark-background-300/70 absolute top-2/6 right-2 z-10 transform cursor-pointer rounded-full p-2 shadow-lg backdrop-blur-xl">
								<ChevronRightIcon className="h-6 w-6 text-black dark:text-white" />
							</div>
						</>
					)}
				</div>
			</div>

			{/* Product Details */}
			<div className="flex w-full flex-col lg:w-2/3">
				<div className={"mb-4 px-3 sm:px-6"}>
					{/* Product Title */}
					<h1
						id="productHeroTitle"
						className="text-2xl font-bold text-pretty text-gray-800 dark:text-gray-100">
						{props.product.title || props.product.name}
					</h1>

					{/* Product Category */}
					{props.product.brand && (
						<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
							<span className="font-medium text-gray-700 dark:text-gray-300">
								{props.product.brand.name}
							</span>
                            {props.product.full_rrp && (
                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                    &nbsp;| UVP: {props.product.full_rrp}
                                </span>
                            )}
						</p>
					)}

					{/* Product Description */}
					<p
						ref={descRef}
						className={`relative mt-4 overflow-hidden text-pretty hyphens-auto text-gray-600 transition-all duration-300 dark:text-gray-300 ${
							descExpanded ? "line-clamp-none" : "line-clamp-4 sm:line-clamp-2"
						}`}>
						{props.product.desc ??
							`${
								props.product.title ?? props.product.name
							} bei Bestell.bar: Wir überwachen die Verfügbarkeit und benachrichtigen dich sofort, sobald das Produkt gekauft werden kann.`}
						{isOverflowing && !descExpanded && (
							<button
								onClick={() => setDescExpanded((prev) => !prev)}
								className="text-primary-500 via-background-100 to-background-100 dark:via-dark-background-300 dark:to-dark-background-300 absolute right-0 bottom-0 mt-1 cursor-pointer bg-gradient-to-r from-transparent pl-24 underline select-none"
								aria-expanded={descExpanded}>
								...mehr anzeigen
							</button>
						)}
					</p>
				</div>

				<div className="flex flex-col gap-3">
					{props.product_siblings && props.product_siblings.length > 0 && (
						<div className={"my-4"}>
							<BB_Select
								product={props.product}
								parent_product={props.parent_product}
								products={props.product_siblings}
							/>
						</div>
					)}

					{props.buybox_link && (
						<div className="flex flex-col gap-1 border-dashed px-3 text-gray-600 sm:px-6 dark:text-gray-300">
							<BB_Button
								type="available"
								title={
									props.buybox_link.full_price
										? `${props.buybox_link.full_price} bei ${props.buybox_link.store.name}`
										: `Bei ${props.buybox_link.store.name} kaufen`
								}
								icon={CartIcon}
								buttonClassName={"flex-1 min-w-50 max-w-72"}
								iconClassName={"h-5 mr-2 pointer-events-none"}
								onClick={() => {
									if (props.buybox_link?.go_link) {
										window.open(props.buybox_link?.go_link, "_blank");
									}
								}}
							/>
						</div>
					)}

					{props.isFollowedAll ? (
						<BB_Button
							type="unknown"
							title={props.isSubscribeAllLoading ? "" : "Alle Alarme deaktivieren"}
							icon={props.isSubscribeAllLoading ? SpinnerIcon : RemoveBellIcon}
							buttonClassName={"mx-3 sm:mx-6 min-w-50 max-w-72"}
							iconClassName={
								props.isSubscribeAllLoading
									? "h-5 w-5 pointer-events-none text-gray-200 animate-spin dark:text-gray-600 fill-primary-500"
									: "h-5 w-5 mr-2 pointer-events-none"
							}
							onClick={props.handleSubscribleAll}
							disabled={props.buttonsDisabled}
						/>
					) : (
						<BB_Button
							type="notify"
							title={props.isSubscribeAllLoading ? "" : "Alle Alarme aktivieren"}
							icon={props.isSubscribeAllLoading ? SpinnerIcon : BellIcon}
							buttonClassName={"mx-3 sm:mx-6 min-w-50 max-w-72"}
							iconClassName={
								props.isSubscribeAllLoading
									? "h-5 w-5 pointer-events-none text-gray-200 animate-spin dark:text-gray-600 fill-primary-500"
									: "h-5 w-5 mr-2 pointer-events-none"
							}
							onClick={props.handleSubscribleAll}
							disabled={props.buttonsDisabled}
						/>
					)}
				</div>
			</div>
		</section>
	);
};

export default memo(BB_ProductHero);
